import React, { useState, useMemo } from 'react';
import Select from 'react-select';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import countryList from 'react-select-country-list';
import AdImg from './images/ad.webp';
import AdMobileImg from './images/admobile.jpeg';

import ArrowIcon from './images/icons/arrow.svg';
import './css/ad.css';
import { useLanguage } from '../context/LanguageContext';
import { consultationAPI } from '../services/apiService';

const Ad = () => {
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [country, setCountry] = useState(null);
  const [phone, setPhone] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    nationality: '',
    service: ''
  });
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [isSubmitting, setIsSubmitting] = useState(false);

  const options = useMemo(() => countryList().getData(), []);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSubmitStatus(null);
  };

  const changeHandler = value => {
    setCountry(value);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await consultationAPI.submit({
        ...formData,
        country: country?.label || '',
        phone: phone
      });

      setSubmitStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        nationality: '',
        service: ''
      });
      setPhone('');
      setCountry(null);
      setTimeout(() => {
        closeModal();
      }, 2000);
    } catch (error) {
      console.error('Error submitting consultation:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className='ad-section'>
        <div className='overlay'></div>
        <img className='back-img desktop-img' src={AdImg} alt='ad image' />
        <img className='back-img mobile-img' src={AdMobileImg} alt='ad mobile image' />
        <div className='container'>
            <div className='ad-content'> 
                <div className='text'>
                    <h1> {t('ad.title')} </h1>
                    <button 
                        className='start-now-btn' 
                        onClick={openModal}
                        aria-label="Start your business journey"
                    > 
                        {t('ad.startNow')} <img src={ArrowIcon} alt='arrow icon' />  
                    </button>
                </div>
            </div>
        </div>
        
        {/* Modal */}
        <div 
            className={`modal-container ${isModalOpen ? 'show' : 'hide'}`}
            role="dialog" 
            aria-labelledby="modal-title" 
            aria-hidden={!isModalOpen}
            onClick={closeModal}
            style={{
                opacity: isModalOpen ? 1 : 0,
                visibility: isModalOpen ? 'visible' : 'hidden',
                transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out'
            }}
        >
             <div className="modal" onClick={(e) => e.stopPropagation()}>
                <button 
                    className="close-btn" 
                    onClick={closeModal}
                    aria-label="Close modal"
                >
                    ×
                </button>
                <form onSubmit={handleSubmit}>
                    <h2 id="modal-title">
                        {t('hero.modalTitle')}
                    </h2>
                    
                    {submitStatus === 'success' && (
                        <div className="alert alert-success">
                            Thank you! Your consultation request has been sent successfully.
                        </div>
                    )}
                    
                    {submitStatus === 'error' && (
                        <div className="alert alert-error">
                            Sorry, there was an error sending your request. Please try again.
                        </div>
                    )}
                    
                    {/* Country Selector */}
                    <label htmlFor="country-select">{t('hero.countrySelect')}</label>
                    <Select
                        id="country-select"
                        options={options}
                        value={country}
                        onChange={changeHandler}
                        placeholder={t('hero.countryPlaceholder')}
                        aria-label={t('hero.countrySelect')}
                        isClearable
                        isSearchable
                    />

                    <label htmlFor="personal-info"> {t('hero.personalInfo')}</label>

                    <div className='inputs'>
                        <input 
                            type='text' 
                            id="firstName" 
                            name="firstName"
                            placeholder={t('hero.firstName')} 
                            value={formData.firstName}
                            onChange={handleInputChange}
                            required 
                            aria-label={t('hero.firstName')} 
                        />
                        <input 
                            type='text' 
                            id="lastName" 
                            name="lastName"
                            placeholder={t('hero.lastName')} 
                            value={formData.lastName}
                            onChange={handleInputChange}
                            required 
                            aria-label={t('hero.lastName')} 
                        />
                        <input 
                            type='email' 
                            id="email" 
                            name="email"
                            placeholder={t('hero.emailAddress')} 
                            value={formData.email}
                            onChange={handleInputChange}
                            required 
                            aria-label={t('hero.emailAddress')} 
                        />
                        <input 
                            type='text' 
                            id="nationality" 
                            name="nationality"
                            placeholder={t('hero.nationality')} 
                            value={formData.nationality}
                            onChange={handleInputChange}
                            required 
                            aria-label={t('hero.nationality')} 
                        />

                        <PhoneInput
                            country={'us'}
                            value={phone}
                            onChange={setPhone}
                            enableSearch={true}
                            placeholder={t('hero.phoneNumber')}
                            aria-label={t('hero.phoneNumber')}
                            inputProps={{
                                required: true,
                                name: 'phone'
                            }}
                        />
                    </div>

                    <label htmlFor="service-select"> {t('hero.servicesLabel')} </label>
                    <select 
                        id="service-select" 
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        required 
                        aria-label={t('hero.serviceSelect')}
                    >
                        <option value="">{t('hero.serviceSelect')}</option>
                        {/* Original Business Services */}
                        <option value="mainlandCompany">{t('services.mainlandCompany.name')}</option>
                        <option value="freeZoneCompany">{t('services.freeZoneCompany.name')}</option>
                        <option value="proServices">{t('services.proServices.name')}</option>
                        <option value="taxConsultancy">{t('services.taxConsultancy.name')}</option>
                        <option value="financialStatements">{t('services.financialStatements.name')}</option>
                        <option value="documentAttestation">{t('services.documentAttestation.name')}</option>
                        {/* New Services */}
                        <option value="bankAccounts">{t('services.bankAccounts.name')}</option>
                        <option value="visaServices">{t('services.visaServices.name')}</option>
                        <option value="hotelBooking">{t('services.hotelBooking.name')}</option>
                        <option value="flightTickets">{t('services.flightTickets.name')}</option>
                        <option value="websiteDevelopment">{t('services.websiteDevelopment.name')}</option>
                        <option value="mobileAppDevelopment">{t('services.mobileAppDevelopment.name')}</option>
                    </select>

                    <button 
                        type="submit" 
                        className="submit-btn" 
                        aria-label="Submit consultation request"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Sending...' : t('hero.submit')}
                    </button>
                </form>
            </div>
        </div>
    </section>
  )
}

export default Ad
